import classNames from 'classnames';
import React, { useEffect, useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import ReportChildIcon from '@mui/icons-material/Report';
import ReportParentIcon from '@mui/icons-material/ReportOutlined';
import { ClassNameMap, makeStyles } from '@mui/styles';

import {
  EmployeeLicenseStatus,
  EmployeeLicenseWarning,
} from '@vestahealthcare/common/enums';
import { translate } from '@vestahealthcare/common/i18n';
import {
  EmployeeLicense,
  EmployeeLicenseItem,
  PaginationType,
} from '@vestahealthcare/common/models';
import {
  DATE_FORMAT_SHORT,
  EMPTY,
} from '@vestahealthcare/common/utils/constants';

import {
  BackgroundColors,
  CollapseIcon,
  Colors,
  IconButton,
  StatusIndicator,
  Table,
  TableItem,
  Tooltip,
} from 'styleguide-v2';
import { Column } from 'styleguide-v2/src/components/Table';

type EmployeeLicenseTableItem = EmployeeLicenseItem & TableItem;

type Props = {
  defaultPageSize?: number;
  licenses: EmployeeLicenseItem[];
  onEdit: (emplooyee: EmployeeLicenseItem) => void;
  pagination?: PaginationType;
  onChangePage: (page: number) => void;
  onChangeRowsPerPage: (pageSize: number) => void;
};

const useStyles = makeStyles({
  boolean: {
    verticalAlign: 'middle',
  },
  childTable: {
    '&& thead': {
      display: 'none',
    },
    '&& tbody': {
      borderRadius: 0,
      boxShadow: 'none',

      '& tr td': {
        backgroundColor: `${BackgroundColors.lighterGray}!important`,

        '&:first-child': {
          paddingLeft: '1rem',
        },
      },
    },
    '&& tbody tbody': {
      borderLeft: 0,
      borderRight: 0,
    },
  },
  icon: {
    '&&': {
      color: Colors.iconGreen,
      padding: 0,
    },
  },
  table: {
    '&& thead tr th:first-child': {
      paddingLeft: '1rem',
    },
    '&& tbody': {
      '& tr td:first-child': {
        paddingLeft: '1rem',
      },
      '& > tr.detail > td': {
        background: Colors.white,
        padding: '0',
      },
    },
  },
  warningContainer: {
    alignItems: 'center',
    display: 'flex',
    '& > svg': {
      left: '-4.125rem',
      margin: 'auto -2.5rem auto 0',
      position: 'relative',
    },
  },
  warningChildContainer: {
    alignItems: 'center',
    display: 'flex',
    minHeight: '4rem',
    '& > svg': {
      left: '-3.825rem',
      margin: 'auto -2rem auto 0',
      position: 'relative',
    },
  },
});

const getColor = (color?: string) => {
  if (color === 'error') {
    return Colors.textRed;
  }
  if (color === 'warning') {
    return Colors.gold;
  }
  if (color === 'success') {
    return Colors.green;
  }
  return Colors.gray;
};

const EMPLOYEES_LICENSES_COLUMNS = (
  { onEdit }: Props,
  onOpenDetail: (item: EmployeeLicenseTableItem) => void,
  styles: ClassNameMap<'icon' | 'boolean' | 'warningContainer'>,
) => {
  const columns: Column[] = [
    {
      headerName: translate('employees.table.id'),
      component: (item: EmployeeLicenseItem) => {
        const { employee, warning } = item;
        return (
          <div className={styles.warningContainer}>
            {warning && (
              <Tooltip
                text={translate('employees.table.warningLicensesExpiration')}
              >
                <ReportParentIcon
                  sx={{
                    color: Colors.orange,
                    fontSize: 26,
                  }}
                />
              </Tooltip>
            )}
            <span>{employee?.id || EMPTY}</span>
          </div>
        );
      },
      width: 75,
    },
    {
      headerName: translate('employees.table.name'),
      field: 'employee.fullName',
      width: 220,
    },
    {
      headerName: translate('employees.table.psor'),
      component: ({ stateOfResidence }: EmployeeLicenseItem) =>
        stateOfResidence?.toString() || EMPTY,
      width: 250,
    },
    {
      headerName: translate('employees.table.state'),
      component: ({ employee, activeLicenses }: EmployeeLicenseItem) => {
        return activeLicenses.map((item, idx) => (
          <span
            className={!item?.state ? 'semi-bold' : ''}
            key={`employee-${employee.id}-${
              item?.state?.toString() || 'compact'
            }`}
          >
            {item?.state?.toString() ?? translate('employees.table.compact')}
            {activeLicenses.length > idx + 1 ? ', ' : ''}
          </span>
        ));
      },
    },
    {
      headerName: translate('employees.table.lead'),
      field: 'lead.fullName',
    },
    {
      headerName: translate('employees.table.language'),
      component: ({ languages }: EmployeeLicenseItem) =>
        languages
          ?.map((l) => l?.toString())
          ?.filter(Boolean)
          ?.join(', ') || EMPTY,
    },
    {
      headerName: '',
      component: (e: EmployeeLicenseTableItem) => (
        <div className="flex gap">
          <IconButton
            className={styles.icon}
            onClick={() => onEdit(e)}
            size="small"
            tooltip={translate('global.edit')}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            className={styles.icon}
            onClick={() => onOpenDetail(e)}
            size="small"
          >
            <CollapseIcon open={!!e.open} />
          </IconButton>
        </div>
      ),
      width: 70,
    },
  ];

  return columns;
};

const EMPLOYEES_LICENSES_ITEM_COLUMNS = (
  styles: ClassNameMap<'warningContainer' | 'warningChildContainer'>,
) => {
  const columns: Column[] = [
    {
      headerName: ' ',
      component: ({ warning }: EmployeeLicense) => (
        <div className={styles.warningChildContainer}>
          {warning && (
            <Tooltip
              text={translate('employees.table.expiresIn', {
                expires: warning.toString(),
              })}
            >
              <ReportChildIcon
                sx={{
                  color: getColor(EmployeeLicenseWarning.getColor(warning)),
                  fontSize: 20,
                }}
              />
            </Tooltip>
          )}
        </div>
      ),
      width: 75,
    },
    {
      headerName: ' ',
      component: (item: EmployeeLicense) => {
        const { state } = item;
        return (
          <span className={state ? '' : 'semi-bold'}>
            {state?.toString() || 'Compact'}
          </span>
        );
      },
      width: 220,
    },
    {
      headerName: ' ',
      component: ({ status }: EmployeeLicense) =>
        status ? (
          <StatusIndicator
            color={EmployeeLicenseStatus.getColor(status)}
            status={status.toString()}
          />
        ) : (
          <></>
        ),
      width: 300,
    },
    {
      headerName: ' ',
      component: ({ licenseNumber }: EmployeeLicense) => (
        <>
          {licenseNumber && (
            <p className="no-margin">
              {translate('employees.table.licenseNumber')}
              {': '}
              <span className="semi-bold">{licenseNumber}</span>
            </p>
          )}
        </>
      ),
      width: 300,
    },
    {
      headerName: ' ',
      component: ({ issuedAt, expiresAt, warning }: EmployeeLicense) => (
        <>
          {issuedAt && (
            <p className={expiresAt ? '' : 'no-margin'}>
              <span style={{ width: 50 }}>
                {translate('employees.table.issuedAt')}
                {': '}
              </span>
              <span className="semi-bold">
                {issuedAt.format(DATE_FORMAT_SHORT)}
              </span>
            </p>
          )}
          {expiresAt && (
            <p className="no-margin">
              <span style={{ width: 50 }}>
                {translate('employees.table.expiresAt')}
                {': '}
              </span>
              <span className={classNames('semi-bold', warning && 'red')}>
                {expiresAt.format(DATE_FORMAT_SHORT)}
              </span>
            </p>
          )}
        </>
      ),
    },
  ];

  return columns;
};

export const EmployeeLicensesTable = (props: Props) => {
  const {
    defaultPageSize,
    licenses,
    pagination,
    onChangePage,
    onChangeRowsPerPage,
  } = props;
  const styles = useStyles();

  const [data, setData] = useState<EmployeeLicenseTableItem[]>(licenses);

  const onOpenDetail = (item: EmployeeLicenseTableItem) => {
    const itemIdx = data.findIndex((i) => i.employee.id === item.employee.id);
    if (itemIdx === -1) return;

    data[itemIdx].open = !data[itemIdx].open;

    if (data[itemIdx].open) {
      data[itemIdx].detail = (
        <Table
          className={styles.childTable}
          config={{
            columns: EMPLOYEES_LICENSES_ITEM_COLUMNS(styles),
            data: data[itemIdx].activeLicenses,
            paginationOptions: [-1],
          }}
          fontSize="small"
        />
      );
    } else {
      delete data[itemIdx].detail;
    }

    setData([...data]);
  };

  useEffect(() => {
    setData(licenses);
  }, [licenses]);

  return (
    <Table
      className={styles.table}
      config={{
        columns: EMPLOYEES_LICENSES_COLUMNS(props, onOpenDetail, styles),
        data,
        defaultPageSize,
        detail: true,
        pagination,
      }}
      fontSize="small"
      onChangePage={onChangePage}
      onChangeRowsPerPage={onChangeRowsPerPage}
    />
  );
};

export default EmployeeLicensesTable;
